import { useCallback, useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import twclsx from 'utils/twclsx';

/**
 * Hook to manage carousel navigation button states and click handlers
 * @param {Object} emblaApi - Embla carousel API instance
 * @returns {Object} Object containing button states and click handlers
 * @returns {boolean} returns.prevBtnDisabled - Whether previous button is disabled
 * @returns {boolean} returns.nextBtnDisabled - Whether next button is disabled
 * @returns {Function} returns.onPrevButtonClick - Handler for previous button click
 * @returns {Function} returns.onNextButtonClick - Handler for next button click
 */
export const usePrevNextButtons = (emblaApi) => {
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

    const onPrevButtonClick = useCallback(() => {
        if (!emblaApi) return;
        emblaApi.scrollPrev();
    }, [emblaApi]);

    const onNextButtonClick = useCallback(() => {
        if (!emblaApi) return;
        emblaApi.scrollNext();
    }, [emblaApi]);

    const onSelect = useCallback(() => {
        setPrevBtnDisabled(!emblaApi.canScrollPrev());
        setNextBtnDisabled(!emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect(emblaApi);
        emblaApi.on('reInit', onSelect).on('select', onSelect);
    }, [emblaApi, onSelect]);

    return {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    };
};

/**
 * Previous button component for carousel navigation
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {Function} props.onClick - Click handler function
 * @returns {JSX.Element} Previous button component
 */
export function PrevButton({ className, disabled, onClick }) {
    return (
        <button
            className={twclsx(
                'embla__prev flex size-30 items-center justify-center rounded-full bg-off-white md:flex',
                { 'cursor-not-allowed': disabled },
                className
            )}
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label="Previous"
        >
            <ChevronLeftIcon className={twclsx('mr-[2px] size-20', disabled ? 'text-grey-light' : 'text-black')} />
        </button>
    );
}

/**
 * Next button component for carousel navigation
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {Function} props.onClick - Click handler function
 * @returns {JSX.Element} Next button component
 */
export function NextButton({ className, disabled, onClick }) {
    return (
        <button
            className={twclsx(
                'embla__next flex size-30 items-center justify-center rounded-full bg-off-white md:flex',
                { 'cursor-not-allowed': disabled },
                className
            )}
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label="Next"
        >
            <ChevronRightIcon className={twclsx('ml-[2px] size-20', disabled ? 'text-grey-light' : 'text-black')} />
        </button>
    );
}
